/* CrossDivisionTrafficWidget — Admin Overview pane that visualises which
 * division feeds traffic to which, sourced from /api/admin/cross-division-
 * traffic over the selected window.
 *
 * Layout: [Total clicks] + [Top flows ranked list] + a 4×4 from→to grid
 * where the cell intensity reflects volume. Diagonal is dimmed (no
 * self-clicks). Range toggle (7 / 30 / 90 days) mirrors UnifiedRevenueWidget.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, RefreshCw, TrendingUp, Network, MousePointerClick } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const DIVISIONS = [
  { id: 'home',     label: 'Home',     accent: '#94a3b8' },
  { id: 'systems',  label: 'Systems',  accent: '#3b82f6' },
  { id: 'services', label: 'Services', accent: '#10b981' },
  { id: 'academy',  label: 'Academy',  accent: '#f5a623' },
];
const ID_TO_LABEL = Object.fromEntries(DIVISIONS.map((d) => [d.id, d.label]));

export default function CrossDivisionTrafficWidget() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/cross-division-traffic?days=${days}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Lookup helper for the 4×4 matrix: O(flows) build, O(1) read.
  const flowMap = useMemo(() => {
    const m = new Map();
    (data?.flows || []).forEach((f) => m.set(`${f.from}>${f.to}`, f.count));
    return m;
  }, [data]);

  const peak = useMemo(() => {
    let max = 0;
    flowMap.forEach((v) => { if (v > max) max = v; });
    return max;
  }, [flowMap]);

  return (
    <section
      data-testid="cross-division-traffic-widget"
      className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#FEFBF3] text-[#c9a227] inline-flex items-center justify-center">
            <Network className="w-4.5 h-4.5" />
          </div>
          <div>
            <h3 className="text-[15px] font-extrabold text-gray-900 leading-tight">
              Cross-Division Traffic
            </h3>
            <p className="text-[12px] text-gray-500 m-0">
              Where visitors flow between the 3 divisions and the homepage
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              data-testid={`cross-traffic-range-${d}`}
              className={`px-2.5 py-1 rounded-md text-[11.5px] font-bold uppercase tracking-wider ${
                days === d ? 'bg-[#0e0e10] text-white' : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {d}d
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            data-testid="cross-traffic-refresh"
            aria-label="Refresh"
            className="ml-1 w-7 h-7 rounded-md text-gray-400 hover:text-gray-900 inline-flex items-center justify-center"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">
          {error}
        </div>
      )}

      {!error && (
        <>
          {/* Headline */}
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            <Stat label="Total clicks" value={data?.total_clicks ?? 0} testId="cross-traffic-total" />
            <Stat
              label="Top flow"
              value={data?.flows?.[0]
                ? `${ID_TO_LABEL[data.flows[0].from]} → ${ID_TO_LABEL[data.flows[0].to]}`
                : '—'}
              suffix={data?.flows?.[0] ? `${data.flows[0].count}` : ''}
              testId="cross-traffic-top-flow"
            />
            <Stat
              label="Window"
              value={`Last ${data?.days || days} days`}
              testId="cross-traffic-window"
            />
          </div>

          {/* Top flows ranked list */}
          {data?.flows?.length > 0 && (
            <div className="mb-6" data-testid="cross-traffic-flows">
              <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-2">
                Top flows
              </div>
              <ul className="space-y-1.5">
                {data.flows.slice(0, 5).map((f) => {
                  const pct = peak ? Math.round((f.count / peak) * 100) : 0;
                  return (
                    <li
                      key={`${f.from}>${f.to}`}
                      data-testid={`cross-traffic-flow-${f.from}-${f.to}`}
                      className="flex items-center gap-3 text-[13px]"
                    >
                      <span className="w-[68px] text-right font-semibold text-gray-700 shrink-0">
                        {ID_TO_LABEL[f.from]}
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" />
                      <span className="w-[68px] font-semibold text-gray-700 shrink-0">
                        {ID_TO_LABEL[f.to]}
                      </span>
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#f5a623]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-10 text-right tabular-nums font-bold text-gray-900 text-[12px]">
                        {f.count}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* 4×4 from→to matrix */}
          <div data-testid="cross-traffic-matrix">
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-2">
              From → To matrix
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] border-collapse">
                <thead>
                  <tr>
                    <th className="text-left font-bold text-gray-500 py-1.5 px-2">From / To</th>
                    {DIVISIONS.map((d) => (
                      <th
                        key={d.id}
                        className="text-center font-bold py-1.5 px-2"
                        style={{ color: d.accent }}
                      >
                        {d.label}
                      </th>
                    ))}
                    <th className="text-right font-bold text-gray-500 py-1.5 px-2">Out</th>
                  </tr>
                </thead>
                <tbody>
                  {DIVISIONS.map((src) => (
                    <tr key={src.id} className="border-t border-gray-100">
                      <td
                        className="py-1.5 px-2 font-bold"
                        style={{ color: src.accent }}
                      >
                        {src.label}
                      </td>
                      {DIVISIONS.map((dst) => {
                        const same = src.id === dst.id;
                        const count = flowMap.get(`${src.id}>${dst.id}`) || 0;
                        const intensity = peak ? count / peak : 0;
                        const bg = same
                          ? 'rgba(0,0,0,0.04)'
                          : `rgba(245, 166, 35, ${0.08 + 0.7 * intensity})`;
                        return (
                          <td
                            key={dst.id}
                            data-testid={`cross-traffic-cell-${src.id}-${dst.id}`}
                            className="text-center py-1.5 px-2 tabular-nums"
                            style={{ background: bg, color: same ? '#9ca3af' : '#0e0e10' }}
                          >
                            {same ? '—' : count || ''}
                          </td>
                        );
                      })}
                      <td className="text-right py-1.5 px-2 font-bold text-gray-900 tabular-nums">
                        {data?.out_totals?.[src.id] || 0}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-gray-200 bg-gray-50">
                    <td className="text-left py-1.5 px-2 font-bold text-gray-500">In</td>
                    {DIVISIONS.map((d) => (
                      <td
                        key={d.id}
                        className="text-center py-1.5 px-2 font-bold text-gray-900 tabular-nums"
                        data-testid={`cross-traffic-in-${d.id}`}
                      >
                        {data?.in_totals?.[d.id] || 0}
                      </td>
                    ))}
                    <td className="text-right py-1.5 px-2 font-extrabold text-gray-900 tabular-nums">
                      {data?.total_clicks || 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {data && data.total_clicks === 0 && (
              <p className="mt-3 text-[12.5px] text-gray-500 inline-flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" /> No cross-division clicks yet.
                Visitors who click any &ldquo;More from ZHELTA&rdquo; tile will start populating this widget.
              </p>
            )}
          </div>

          {/* Recommendation CTR (impressions vs clicks per item) */}
          {data?.reco_ctr?.length > 0 && (
            <div className="mt-7 pt-6 border-t border-gray-100" data-testid="cross-traffic-reco-ctr">
              <div className="flex items-baseline justify-between mb-3 gap-3">
                <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 inline-flex items-center gap-1.5">
                  <MousePointerClick className="w-3 h-3" />
                  Recommendation CTR
                </div>
                <div className="text-[11.5px] text-gray-500 tabular-nums">
                  <span className="font-bold text-gray-900">{data.reco_total_clicks}</span>
                  {' / '}
                  <span className="font-bold text-gray-900">{data.reco_total_impressions}</span>
                  {' clicks · '}
                  <span className="font-bold text-gray-900">
                    {data.reco_total_impressions
                      ? `${Math.round((data.reco_total_clicks / data.reco_total_impressions) * 1000) / 10}%`
                      : '0%'}
                  </span>
                  {' overall'}
                </div>
              </div>
              <ul className="space-y-1.5">
                {data.reco_ctr.slice(0, 6).map((r) => {
                  const pct = Math.min(100, r.ctr);
                  return (
                    <li
                      key={r.item_id}
                      data-testid={`cross-traffic-reco-${r.item_id}`}
                      className="flex items-center gap-3 text-[13px]"
                    >
                      <span className="w-[140px] truncate font-semibold text-gray-700 shrink-0" title={r.label}>
                        {r.label}
                      </span>
                      <span className="w-[68px] text-[10.5px] uppercase tracking-[0.16em] font-extrabold text-gray-400 shrink-0">
                        {r.division}
                      </span>
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-12 text-right tabular-nums font-bold text-gray-900 text-[12px]">
                        {r.ctr}%
                      </span>
                      <span className="w-16 text-right tabular-nums text-[11.5px] text-gray-500">
                        {r.clicks}/{r.impressions}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-[11.5px] text-gray-400">
                Sorted by CTR. Items with low CTR over a long window are candidates to swap out of the recommendation catalog.
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value, suffix, testId }) {
  return (
    <div
      className="rounded-xl border border-gray-200 bg-[#FEFBF3] px-4 py-3"
      data-testid={testId}
    >
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-[18px] font-extrabold text-gray-900 leading-tight">
        {value}
        {suffix && <span className="ml-2 text-[12px] font-bold text-[#c9a227]">{suffix}</span>}
      </div>
    </div>
  );
}
